/**
 * Leveling / XP curves — the real, documented mainline mechanics.
 *
 * `expForLevel(rate, level)` returns the TOTAL accumulated experience required to
 * *reach* `level` on a growth curve (so `expForLevel(rate, 1) === 0`). All six
 * canonical curves are implemented with their exact integer formulas; the two
 * piecewise curves (erratic, fluctuating) match the documented branch points.
 *
 * See @/shared/leveling for the frozen contract.
 */
import type {
  GrowthRate,
  ExpShareConfig,
  LevelUpEvent,
  SpeciesData,
  Nature,
  PokemonInstance,
  StatKey,
  EVs,
} from '@/shared';
import { LEVEL_MIN, LEVEL_MAX, STAT_KEYS, EV_MAX_PER_STAT, EV_MAX_TOTAL } from '@/shared';
import { computeStats } from './stats.js';

/**
 * Total EXP required to *reach* `level` on a growth curve. `expForLevel(rate, 1) === 0`.
 *
 * Formulas (n = level), per the documented mainline mechanics:
 *  - medium-fast: n^3
 *  - fast:        floor(4 n^3 / 5)
 *  - slow:        floor(5 n^3 / 4)
 *  - medium-slow: floor(6/5 n^3 - 15 n^2 + 100 n - 140)
 *  - erratic & fluctuating: piecewise (see below)
 */
export function expForLevel(rate: GrowthRate, level: number): number {
  const n = Math.trunc(level);
  if (n <= 1) return 0;

  switch (rate) {
    case 'medium-fast':
      return n * n * n;
    case 'fast':
      return Math.floor((4 * n * n * n) / 5);
    case 'slow':
      return Math.floor((5 * n * n * n) / 4);
    case 'medium-slow':
      return Math.floor((6 / 5) * n * n * n - 15 * n * n + 100 * n - 140);
    case 'erratic':
      return erratic(n);
    case 'fluctuating':
      return fluctuating(n);
    default: {
      // Exhaustiveness guard — unreachable for valid GrowthRate values.
      const never: never = rate;
      throw new Error(`Unknown growth rate: ${String(never)}`);
    }
  }
}

const cube = (n: number): number => n * n * n;

/** Erratic curve — four pieces split at 50, 68 and 98. */
function erratic(n: number): number {
  if (n <= 50) {
    return Math.floor((cube(n) * (100 - n)) / 50);
  }
  if (n <= 68) {
    return Math.floor((cube(n) * (150 - n)) / 100);
  }
  if (n <= 98) {
    return Math.floor((cube(n) * Math.floor((1911 - 10 * n) / 3)) / 500);
  }
  // n >= 99
  return Math.floor((cube(n) * (160 - n)) / 100);
}

/** Fluctuating curve — three pieces split at 15 and 36. */
function fluctuating(n: number): number {
  if (n <= 15) {
    return Math.floor((cube(n) * (Math.floor((n + 1) / 3) + 24)) / 50);
  }
  if (n <= 36) {
    return Math.floor((cube(n) * (n + 14)) / 50);
  }
  return Math.floor((cube(n) * (Math.floor(n / 2) + 32)) / 50);
}

/**
 * Level a mon would be at given total accumulated EXP on a curve. The result is
 * the highest level whose threshold is `<= exp`, clamped to [LEVEL_MIN, LEVEL_MAX].
 */
export function levelForExp(rate: GrowthRate, exp: number): number {
  if (exp <= 0) return LEVEL_MIN;
  if (exp >= expForLevel(rate, LEVEL_MAX)) return LEVEL_MAX;
  // Thresholds are strictly increasing, so a simple scan up from LEVEL_MIN is
  // exact and cheap (at most 99 iterations).
  let level = LEVEL_MIN;
  while (level < LEVEL_MAX && expForLevel(rate, level + 1) <= exp) {
    level++;
  }
  return level;
}

/** Alias kept for call-site readability (gaining battle EXP → a new level). */
export const levelFromExp = levelForExp;

/**
 * Every level-up move a species naturally knows by `level`, in learn order (ascending
 * level, then learnset order for ties), deduped. This is the FULL natural moveset for a
 * mon at that level — the basis for the unlimited-moveset save fixup (a mon keeps every
 * move it would have learned along the way, not just the most-recent four).
 *
 * Pure + deterministic; no rendering/IO. Non-level-up entries (machine/tutor/egg/evo)
 * are ignored. Entries without a `level` are treated as level 1 (start moves).
 */
export function naturalLearnsetUpToLevel(species: SpeciesData, level: number): string[] {
  const lvl = Math.max(1, Math.trunc(level));
  const eligible = species.learnset
    .map((entry, order) => ({ entry, order }))
    .filter(({ entry }) => entry.method === 'level-up' && (entry.level ?? 1) <= lvl)
    .sort((a, b) => (a.entry.level ?? 1) - (b.entry.level ?? 1) || a.order - b.order);

  const out: string[] = [];
  for (const { entry } of eligible) {
    if (!out.includes(entry.moveId)) out.push(entry.moveId);
  }
  return out;
}

/**
 * Generosity multiplier on the per-battle base EXP (CLAUDE.md pillar 6: a generous,
 * brisk curve so leveling feels rewarding and frequent — "too hard" was the owner's
 * complaint). Tasteful, not instant: 2.5× roughly *more than doubles* normal-play XP
 * over the faithful mainline base, so a relaxed expedition levels at a satisfying clip
 * without trivializing the satisfying climb. Applied in {@link expGainFromBattle}.
 */
export const EXP_GENEROSITY = 2.5;

/**
 * Default whole-team EXP-share config (pillar 6: nobody left behind, no babysitting).
 *
 * `benchedShare` is GENEROUS (0.75): a benched team member earns three-quarters of the
 * base, so the whole team grows together and nothing you catch is dead weight — yet
 * sending a mon out still rewards it with the full base.
 */
export const DEFAULT_EXP_SHARE: ExpShareConfig = {
  benchedShare: 0.75,
  shareWithFainted: false,
};

/** The defeated mon's EXP-relevant facts. */
export interface DefeatedYield {
  /** The defeated species' base experience yield. */
  readonly baseExpYield: number;
  /** The level the defeated mon was at. */
  readonly defeatedLevel: number;
}

/** One team member's eligibility for a share of a battle's EXP. */
export interface ExpShareMember {
  readonly instanceId: string;
  /** True if this mon was sent out / took part in the KO. */
  readonly participated: boolean;
  /** True if this mon is currently fainted. */
  readonly fainted: boolean;
}

/** How much EXP a given team member earned from a battle. */
export interface ExpAward {
  readonly instanceId: string;
  readonly exp: number;
}

/**
 * Award a battle's EXP across the whole team (CLAUDE.md pillar 6 — nobody is left
 * behind, no babysitting).
 *
 * Base, faithful to the mainline games but scaled GENEROUSLY (pillar 6 — leveling
 * should feel brisk + rewarding, not a grind):
 *   base = floor(EXP_GENEROSITY * baseExpYield * defeatedLevel / 7)
 *
 * `EXP_GENEROSITY` (2.5) is the single generosity dial; with it off (1.0) the formula
 * is the exact mainline base. A caller may pass a per-battle `generosity` override
 * (e.g. a Training-Grounds session that wants an extra-rewarding practice wave).
 *
 * Share rule (generous, EXP-share-style, deterministic — no RNG):
 *   - participants earn the full base;
 *   - non-participating, non-fainted bench mons earn floor(base * benchedShare)
 *     (DEFAULT_EXP_SHARE.benchedShare = 0.75 — generous whole-team growth, but
 *     sending a mon out still earns it more);
 *   - fainted mons are skipped unless `shareWithFainted` is set, in which case they
 *     are treated as bench mons (floor(base * benchedShare)).
 *
 * Order of awards mirrors the input `members` order.
 */
export function expGainFromBattle(
  defeated: DefeatedYield,
  members: readonly ExpShareMember[],
  config: ExpShareConfig = DEFAULT_EXP_SHARE,
  generosity: number = EXP_GENEROSITY,
): ExpAward[] {
  const base = Math.floor((generosity * defeated.baseExpYield * defeated.defeatedLevel) / 7);
  const benched = Math.floor(base * config.benchedShare);

  const awards: ExpAward[] = [];
  for (const m of members) {
    if (m.fainted && !config.shareWithFainted) continue;
    const exp = m.participated ? base : benched;
    awards.push({ instanceId: m.instanceId, exp });
  }
  return awards;
}

/**
 * Apply a new total-EXP value to an owned mon and surface the juicy growth events.
 *
 * Mutates the instance: sets `exp`, advances `level` if any boundary was crossed,
 * recomputes and caches `maxHp` (via core/stats), and raises `currentHp` by the HP
 * gain (a damaged mon is not fully healed by a level-up — only the gained HP is added).
 *
 * Returns a single `LevelUpEvent` covering the whole jump (from old level to new
 * level), with per-stat deltas and every `level-up` move learnable across the levels
 * gained (deduped, in learn order). Returns an empty array when no boundary is crossed.
 *
 * `newMoves` lists EVERY move crossed — the consumer simply ADDS them all to the mon's
 * (unbounded) moveset; there is no 4-move cap and no replace prompt.
 */
export function makeLevelUpEvents(
  instance: PokemonInstance,
  newExp: number,
  species: SpeciesData,
  nature: Nature,
): LevelUpEvent[] {
  const rate = species.growthRate;
  const fromLevel = instance.level;
  const toLevel = levelForExp(rate, newExp);

  instance.exp = newExp;

  if (toLevel <= fromLevel) {
    return [];
  }

  const before = computeStats(species.baseStats, instance.ivs, instance.evs, fromLevel, nature);
  const after = computeStats(species.baseStats, instance.ivs, instance.evs, toLevel, nature);

  const statGains: Record<string, number> = {};
  for (const key of STAT_KEYS as readonly StatKey[]) {
    statGains[key] = after[key] - before[key];
  }

  const newMoves: string[] = [];
  for (const entry of species.learnset) {
    if (
      entry.method === 'level-up' &&
      entry.level !== undefined &&
      entry.level > fromLevel &&
      entry.level <= toLevel
    ) {
      if (!newMoves.includes(entry.moveId)) newMoves.push(entry.moveId);
    }
  }

  instance.level = toLevel;
  instance.maxHp = after.hp;
  instance.currentHp += after.hp - before.hp;

  return [
    {
      kind: 'level-up',
      instanceId: instance.id,
      fromLevel,
      toLevel,
      statGains,
      newMoves,
    },
  ];
}

// ── EXP bar (the classic blue gauge under the HP bar) ──────────────────────────────

/**
 * The fraction (0..1) an owned mon has filled toward its NEXT level — the classic blue
 * EXP gauge. This is the progress *within* the current level:
 *
 *   frac = (exp - expForLevel(rate, level)) / (expForLevel(rate, level+1) - expForLevel(rate, level))
 *
 * Clamped to [0, 1]. A mon at LEVEL_MAX (or with a degenerate zero-width span) reads a
 * full bar (1). Pure — the renderer tweens the gauge between successive fractions and
 * wraps it to 0 on each level-up.
 */
export function expBarFraction(rate: GrowthRate, level: number, exp: number): number {
  if (level >= LEVEL_MAX) return 1;
  const base = expForLevel(rate, level);
  const next = expForLevel(rate, level + 1);
  const span = next - base;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (exp - base) / span));
}

// ── Effort Values on KO (the quiet min-max layer, real mechanic) ────────────────────

const ZERO_EVS: EVs = { hp: 0, atk: 0, def: 0, spatk: 0, spdef: 0, spe: 0 };

/**
 * A defeated species' EV yield (all zeros when the species/data omits it). Kept as a
 * helper so both the award path and any UI read the same defaulted shape.
 */
export function evYieldOf(species: Pick<SpeciesData, 'evYield'>): EVs {
  const y = species.evYield;
  if (!y) return { ...ZERO_EVS };
  return {
    hp: y.hp ?? 0,
    atk: y.atk ?? 0,
    def: y.def ?? 0,
    spatk: y.spatk ?? 0,
    spdef: y.spdef ?? 0,
    spe: y.spe ?? 0,
  };
}

/**
 * Add an EV yield onto a mon's current EVs, honoring BOTH standard caps — 252 per stat
 * and 510 total — exactly like the mainline games. Stats are filled in canonical order
 * (HP → Speed); once the 510 total is reached, later stats simply stop gaining. Pure:
 * returns a NEW EVs object, mutating nothing.
 */
export function awardEvs(current: EVs, add: Partial<EVs>): EVs {
  const out: Record<StatKey, number> = { ...current };
  let total = (STAT_KEYS as readonly StatKey[]).reduce((s, k) => s + out[k], 0);
  for (const k of STAT_KEYS as readonly StatKey[]) {
    const want = add[k] ?? 0;
    if (want <= 0) continue;
    const perStatRoom = EV_MAX_PER_STAT - out[k];
    const totalRoom = EV_MAX_TOTAL - total;
    const gain = Math.max(0, Math.min(want, perStatRoom, totalRoom));
    if (gain <= 0) continue;
    out[k] += gain;
    total += gain;
  }
  return out;
}

// ── Friendship (a real, quiet mechanic — never a nag) ──────────────────────────────

/** Max friendship/happiness (mainline 0–255). */
export const FRIENDSHIP_MAX = 255;
/** Friendship gained by a team member that took part in a battle win. */
export const FRIENDSHIP_ON_WIN = 2;
/** Friendship gained when a mon levels up. */
export const FRIENDSHIP_ON_LEVEL_UP = 3;
/** Friendship gained on a gym-class / challenge win (reserved hook). */
export const FRIENDSHIP_ON_GYM_WIN = 5;

/** Apply a friendship delta, clamped to [0, {@link FRIENDSHIP_MAX}]. Pure. */
export function friendshipAfter(current: number, delta: number): number {
  return Math.max(0, Math.min(FRIENDSHIP_MAX, current + delta));
}
